import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, MousePointerClick, CalendarCheck, TrendingUp } from 'lucide-react';

interface ActivityStatsPanelProps {
  eventId: string;
}

interface StatsResponse {
  clicks: number;
  bookings_started: number;
  bookings_completed: number;
  conversion_rate: number;
  period_days: number;
}

function StatItem({
  icon: Icon,
  label,
  value,
  suffix,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-medium tabular-nums">
          {value}
          {suffix && <span className="text-sm text-muted-foreground ml-0.5">{suffix}</span>}
        </p>
      </div>
    </div>
  );
}

export function ActivityStatsPanel({ eventId }: ActivityStatsPanelProps) {
  const [days, setDays] = useState<'7' | '30'>('7');

  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['activity-stats', eventId, days],
    queryFn: async (): Promise<StatsResponse> => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(
        `${baseUrl}/functions/v1/analytics-proxy?action=activity&event_id=${eventId}&days=${days}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!res.ok) {
        await res.text();
        throw new Error(`Stats fetch failed: ${res.status}`);
      }

      return res.json();
    },
    staleTime: 5 * 60_000, // 5 min
    retry: 1,
  });

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            Statistik
          </CardTitle>
          <ToggleGroup
            type="single"
            value={days}
            onValueChange={(v) => v && setDays(v as '7' | '30')}
            className="gap-0"
          >
            <ToggleGroupItem value="7" className="text-xs h-7 px-2.5">
              7 dagar
            </ToggleGroupItem>
            <ToggleGroupItem value="30" className="text-xs h-7 px-2.5">
              30 dagar
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground py-4">
            Kunde inte hämta statistik just nu. Försök igen senare.
          </p>
        ) : stats && (stats.clicks > 0 || stats.bookings_started > 0 || stats.bookings_completed > 0) ? (
          <div className="divide-y divide-border">
            <StatItem
              icon={MousePointerClick}
              label="Klick från sociala medier"
              value={stats.clicks}
            />
            <StatItem
              icon={CalendarCheck}
              label="Bokningar startade"
              value={stats.bookings_started}
            />
            <StatItem
              icon={CalendarCheck}
              label="Bokningar genomförda"
              value={stats.bookings_completed}
            />
            <StatItem
              icon={TrendingUp}
              label="Konvertering"
              value={stats.conversion_rate}
              suffix="%"
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">
            Ingen statistik ännu. Dela din aktivitet för att börja samla data.
          </p>
        )}

        <p className="text-[11px] text-muted-foreground mt-3">
          Visar trafik från UTM-spårade länkar de senaste {days} dagarna.
        </p>
      </CardContent>
    </Card>
  );
}
